<template>
  <div class="container-fluid mt-4 fitdays-view">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div>
        <h2 class="mb-0"><i class="bi bi-speedometer2 me-2"></i>FitDays</h2>
        <small class="text-muted">Composición corporal desde tu báscula · API no oficial ICOMON</small>
        <div v-if="status?.configured && status.lastSyncAt" class="small text-muted mt-1">
          Última sincronización: {{ formatSyncAt(status.lastSyncAt) }}
        </div>
      </div>
      <div class="d-flex gap-2 flex-wrap" v-if="status?.configured">
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="sincronizando"
          @click="sincronizar(90)"
        >
          <span v-if="sincronizando && syncDays === 90" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-repeat me-1"></i>
          Sync 90 días
        </button>
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="sincronizando"
          @click="sincronizar(365)"
        >
          <span v-if="sincronizando && syncDays === 365" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-cloud-download me-1"></i>
          Sync 1 año
        </button>
        <router-link to="/perfil" class="btn btn-outline-secondary">
          <i class="bi bi-gear me-1"></i>
          Credenciales
        </router-link>
      </div>
    </div>

    <div v-if="status?.configured && status.hasSession === false" class="alert alert-warning">
      No hay sesión FitDays guardada. Vuelve a guardar las credenciales en
      <router-link to="/perfil">Perfil</router-link>.
      La sincronización no inicia sesión sola, para no cerrar la app del móvil.
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="mensaje" class="alert alert-success">{{ mensaje }}</div>

    <div v-if="!status?.configured" class="card border-0 bg-light">
      <div class="card-body">
        <h5 class="card-title">Configura FitDays</h5>
        <p class="mb-2 small">
          Añade el email y la contraseña de tu cuenta FitDays en
          <router-link to="/perfil">Editar perfil</router-link>
          (sección FitDays, solo administradores).
        </p>
        <p class="mb-0 small text-muted">
          Tras guardar las credenciales, vuelve aquí y pulsa sincronizar para importar tus pesadas.
        </p>
      </div>
    </div>

    <template v-else>
      <div v-if="trends?.latest" class="row g-3 mb-4">
        <div class="col-md-4">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body text-center py-4">
              <div class="display-5 fw-bold text-primary">{{ fmtKg(trends.latest.peso_kg) }}</div>
              <div class="text-uppercase small fw-semibold text-muted mt-1">Peso</div>
              <div v-if="ultimaMedicionLabel" class="small text-muted mt-2">{{ ultimaMedicionLabel }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body text-center py-4">
              <div class="display-5 fw-bold">{{ fmtNum(trends.latest.imc) }}</div>
              <div class="text-uppercase small fw-semibold text-muted mt-1">IMC</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body text-center py-4">
              <div class="display-5 fw-bold">{{ fmtPct(trends.latest.grasa_pct) }}</div>
              <div class="text-uppercase small fw-semibold text-muted mt-1">Grasa corporal</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 shadow-sm mb-4">
        <div class="card-header bg-white border-bottom-0 py-3">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <h5 class="mb-0"><i class="bi bi-graph-up me-2"></i>Evolución del peso</h5>
            <div class="d-flex flex-wrap align-items-center gap-2">
              <label class="small text-muted mb-0">Agrupación</label>
              <select
                v-model="groupBy"
                class="form-select form-select-sm"
                style="width: auto; min-width: 10rem"
                :disabled="cargandoTrends"
                @change="cargarTrends"
              >
                <option v-for="g in agrupaciones" :key="g.id" :value="g.id">{{ g.label }}</option>
              </select>
            </div>
          </div>
          <div class="btn-group btn-group-sm flex-wrap" role="group">
            <button
              v-for="w in ventanas"
              :key="w.d"
              type="button"
              class="btn"
              :class="trendsDays === w.d ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="cargandoTrends"
              @click="cambiarVentana(w.d)"
            >
              {{ w.label }}
            </button>
          </div>
        </div>
        <div class="card-body">
          <div v-if="cargandoTrends" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
          </div>
          <div v-else-if="trends?.labels?.length" class="fitdays-chart-wrap">
            <Line :data="chartData" :options="chartOptions" />
          </div>
          <p v-else class="text-muted mb-0 small">
            No hay datos. Pulsa «Sync» para importar mediciones desde FitDays.
          </p>
          <p class="text-muted small mt-3 mb-0">
            <strong>Diario:</strong> última pesada de cada día.
            <strong>Semana / Mes:</strong> media de esos valores diarios en cada periodo.
          </p>
        </div>
      </div>

      <div class="card border-0 shadow-sm">
        <div class="card-header bg-white">
          <h5 class="mb-0"><i class="bi bi-table me-2"></i>Últimas mediciones</h5>
        </div>
        <div class="card-body p-0">
          <div v-if="cargandoTabla" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
          </div>
          <div v-else-if="filas.length" class="table-responsive">
            <table class="table table-sm table-hover mb-0 align-middle">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Peso (kg)</th>
                  <th>IMC</th>
                  <th>Grasa %</th>
                  <th>Agua %</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filas" :key="row.measured_time">
                  <td>{{ formatDiaHora(row) }}</td>
                  <td>{{ fmtKg(row.peso_kg) }}</td>
                  <td>{{ fmtNum(row.imc) }}</td>
                  <td>{{ fmtPct(row.grasa_pct) }}</td>
                  <td>{{ fmtPct(row.agua_pct) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted small p-3 mb-0">Sin registros locales.</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from '../config/axios';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LineController,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, LineElement, PointElement, LineController, Filler);

const COL_PESO = '#0d6efd';

export default {
  name: 'SaludFitDays',
  components: { Line },
  data() {
    return {
      status: null,
      trends: null,
      filas: [],
      error: null,
      mensaje: null,
      sincronizando: false,
      syncDays: 0,
      cargandoTrends: false,
      cargandoTabla: false,
      trendsDays: 365,
      groupBy: 'week',
      agrupaciones: [
        { id: 'day', label: 'Diario' },
        { id: 'week', label: 'Semana (media)' },
        { id: 'month', label: 'Mes (media)' },
      ],
      ventanas: [
        { d: 90, label: '90 d' },
        { d: 180, label: '180 d' },
        { d: 365, label: '1 año' },
        { d: 730, label: '2 años' },
      ],
      pollingTimer: null,
      pollingPrev: {
        ultimoDia: null,
        mediciones: null,
      },
    };
  },
  computed: {
    ultimaMedicionLabel() {
      const at = this.status?.ultimaMedicion?.measured_at
        || this.trends?.latest?.measured_at
        || this.trends?.latest?.measured_time
        || null;
      if (at == null || at === '') return '';
      const raw = String(at).trim();
      if (/^\d+$/.test(raw)) {
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) return '';
        const ms = n < 1e12 ? n * 1000 : n;
        return this.formatSyncAt(new Date(ms).toISOString());
      }
      return this.formatSyncAt(at);
    },
    chartData() {
      const t = this.trends;
      if (!t?.labels?.length) return { labels: [], datasets: [] };
      const labelText =
        this.groupBy === 'day' ? 'Peso (kg)' : this.groupBy === 'week' ? 'Peso semanal (media kg)' : 'Peso mensual (media kg)';
      return {
        labels: t.labels.map((raw) => this.formatChartLabel(raw)),
        datasets: [
          {
            label: labelText,
            data: t.series.peso,
            borderColor: COL_PESO,
            backgroundColor: 'rgba(13, 110, 253, 0.08)',
            fill: true,
            tension: 0.25,
            spanGaps: true,
            pointRadius: this.groupBy === 'day' ? 2 : 3,
            pointHoverRadius: 5,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.y;
                if (v == null) return `${ctx.dataset.label}: —`;
                return `${ctx.dataset.label}: ${v.toFixed(2)} kg`;
              },
            },
          },
        },
        scales: {
          y: {
            grid: { color: 'rgba(0,0,0,0.06)' },
            title: { display: true, text: 'kg' },
          },
          x: {
            grid: { display: false },
            ticks: {
              maxRotation: 45,
              autoSkip: true,
              maxTicksLimit: this.groupBy === 'day' ? 24 : 36,
            },
          },
        },
      };
    },
  },
  async mounted() {
    await this.cargarStatus();
    if (this.status?.configured) {
      await Promise.all([this.cargarTrends(), this.cargarTabla()]);
    }

    this.startAutoRefresh();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  methods: {
    fmtKg(v) {
      if (v == null || v === '') return '—';
      return `${Number(v).toFixed(1)} kg`;
    },
    fmtNum(v) {
      if (v == null || v === '') return '—';
      return Number(v).toFixed(1);
    },
    fmtPct(v) {
      if (v == null || v === '') return '—';
      return `${Number(v).toFixed(1)}%`;
    },
    formatDia(iso) {
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    },
    /**
     * SQLite datetime('now') y toISOString() se guardan en UTC.
     * Mostrar siempre en Europe/Madrid para no parecer "2 h atrasado" en verano.
     */
    formatSyncAt(raw) {
      if (!raw) return '';
      const s = String(raw).trim();
      let iso = s;
      if (!/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
        iso = s.includes('T') ? `${s}Z` : `${s.replace(' ', 'T')}Z`;
      }
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return s;
      return d.toLocaleString('es-ES', {
        timeZone: 'Europe/Madrid',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatDiaHora(row) {
      if (!row?.measured_time) return row?.dia || '—';
      const d = new Date(row.measured_time * 1000);
      return d.toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatChartLabel(raw) {
      if (this.groupBy === 'month' && /^\d{4}-\d{2}$/.test(raw)) {
        const [y, m] = raw.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
      }
      if (this.groupBy === 'week' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return this.formatDia(raw);
      }
      return this.formatDia(raw);
    },
    async cargarStatus() {
      try {
        const { data } = await axios.get('/api/fitdays/status');
        this.status = data;
      } catch (e) {
        this.error = e.response?.data?.error || e.message;
      }
    },
    async cargarTrends() {
      this.cargandoTrends = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/fitdays/trends', {
          params: { days: this.trendsDays, groupBy: this.groupBy },
        });
        this.trends = data;
      } catch (e) {
        this.error = e.response?.data?.error || e.message;
      } finally {
        this.cargandoTrends = false;
      }
    },
    async cargarTabla() {
      this.cargandoTabla = true;
      try {
        const { data } = await axios.get('/api/fitdays/mediciones', { params: { limit: 30 } });
        this.filas = data.rows || [];
      } catch (e) {
        this.error = e.response?.data?.error || e.message;
      } finally {
        this.cargandoTabla = false;
      }
    },
    cambiarVentana(d) {
      this.trendsDays = d;
      this.cargarTrends();
    },
    async sincronizar(days) {
      this.sincronizando = true;
      this.syncDays = days;
      this.error = null;
      this.mensaje = null;
      try {
        const { data } = await axios.post('/api/fitdays/sync', { days }, { timeout: 120000 });
        const latest = data.latest;
        if (latest?.measured_at) {
          const cuando = this.formatSyncAt(latest.measured_at);
          this.mensaje =
            `Sincronizado con la nube FitDays: ${data.upserted} mediciones. ` +
            `Última en cloud: ${this.fmtKg(latest.peso_kg)} (${cuando}). ` +
            `Si acabas de pesarte y no aparece, abre la app FitDays con internet y espera a que suba la pesada.`;
        } else {
          this.mensaje =
            `Sincronizado: ${data.upserted} mediciones. La nube FitDays no devolvió pesos en este periodo.`;
        }
        await this.cargarStatus();
        await Promise.all([this.cargarTrends(), this.cargarTabla()]);
      } catch (e) {
        this.error = e.response?.data?.error || e.message;
      } finally {
        this.sincronizando = false;
        this.syncDays = 0;
      }
    },

    startAutoRefresh() {
      // Recarga "automática" de la parte admin: si entra un nuevo peso (DB cambia),
      // refrescamos tendencias y tabla sin que el admin tenga que pulsar Sync.
      this.stopAutoRefresh();

      const everyMs = 60 * 1000;
      this.pollingPrev.ultimoDia = this.status?.ultimoDia ?? null;
      this.pollingPrev.mediciones = this.status?.mediciones ?? null;

      this.pollingTimer = setInterval(async () => {
        try {
          await this.cargarStatus();

          const ultimoDia = this.status?.ultimoDia ?? null;
          const mediciones = this.status?.mediciones ?? 0;

          const changed = ultimoDia !== this.pollingPrev.ultimoDia || mediciones !== this.pollingPrev.mediciones;
          if (!changed) return;

          this.pollingPrev.ultimoDia = ultimoDia;
          this.pollingPrev.mediciones = mediciones;

          if (this.status?.configured) {
            await Promise.all([this.cargarTrends(), this.cargarTabla()]);
          }
        } catch (e) {
          // Silencioso para no molestar al usuario.
          // (Los errores reales se verán cuando se abra/pulse Sync.)
        }
      }, everyMs);
    },

    stopAutoRefresh() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.fitdays-chart-wrap {
  position: relative;
  height: min(420px, 55vh);
}
</style>
